import React, { useCallback, useState, useEffect, memo } from 'react';
import { fetchSettings, updateSettings } from '../../api/settings';
import { FormGroup, FormToggle, FormInput, HelpText, WarningBox, FormLabel } from '../common/form';
import { motion } from 'framer-motion';

import styles from '../agents/styles/AgentSettings.module.css';

const DEFAULT_SETTINGS = {
    enableServices: true,
    maxToolSteps: 8,
    enableToolSettings: false,
};

const ServicesSettings = () => {
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await fetchSettings();
            setSettings(data.services || DEFAULT_SETTINGS);
        } catch (error) {
            setError('Failed to load settings');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleToggleServices = useCallback(async () => {
        try {
            setIsSaving(true);
            const newSettings = {
                ...settings,
                enableServices: !settings.enableServices
            };
            await updateSettings({ services: newSettings });
            setSettings(newSettings);
        } catch (error) {
            setError('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    }, [settings]);

    const handleMaxStepsChange = useCallback(async e => {
        const value = Math.min(Math.max(parseInt(e.target.value) || 8, 3), 30);
        try {
            setIsSaving(true);
            const newSettings = {
                ...settings,
                maxToolSteps: value
            };
            await updateSettings({ services: newSettings });
            setSettings(newSettings);
        } catch (error) {
            setError('Failed to update max steps');
        } finally {
            setIsSaving(false);
        }
    }, [settings]);

    const handleToggleToolSettings = useCallback(async () => {
        try {
            setIsSaving(true);
            const newSettings = {
                ...settings,
                enableToolSettings: !settings.enableToolSettings
            };
            await updateSettings({ services: newSettings });
            setSettings(newSettings);
        } catch (error) {
            setError('Failed to update tool settings');
        } finally {
            setIsSaving(false);
        }
    }, [settings]);

    if (isLoading) {
        return <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <p>Loading settings...</p>
        </motion.div>
    }

    return (
        <div className={styles.tabContent} style={{ padding: '0 2rem' }}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Services Settings</h3>
                <p className={styles.sectionDescription}>
                    Control how services and tools are used in chats.
                </p>

                <FormGroup>
                    <FormToggle
                        id="enable-services"
                        checked={!!settings.enableServices}
                        onChange={handleToggleServices}
                        disabled={isSaving}
                        title="Enable Services in Chats"
                        description="Allow the use of services and tools in your chat conversations."
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="max-tool-steps">
                        Maximum Tool Steps
                    </FormLabel>
                    <FormInput
                        type="number"
                        id="max-tool-steps"
                        min={3}
                        max={30}
                        value={settings.maxToolSteps}
                        onChange={(v, e) => handleMaxStepsChange(e)}
                        disabled={isSaving}
                        placeholder="8"
                    />
                    <div className={styles.helpRow}>
                        <HelpText>
                            Set the maximum number of steps for tool execution (3-30), default is 8.
                            <br />
                            Each step costs money. This setting allows you to limit the number of steps to prevent large expenses.
                        </HelpText>
                    </div>
                </FormGroup>

                <FormGroup>
                    <FormToggle
                        id="enable-tool-settings"
                        checked={!!settings.enableToolSettings}
                        onChange={handleToggleToolSettings}
                        disabled={isSaving}
                        title="Enable Tool Settings"
                        description="Allow configuration of individual tools for services."
                    />
                </FormGroup>

                <WarningBox>
                    <p>
                        <strong>Warning:</strong> We do not recommend disabling individual tools as this may affect the functionality of services in unexpected ways. Instead, enable or disable entire services as needed.
                    </p>
                </WarningBox>

                {error && (
                    <WarningBox>{error}</WarningBox>
                )}
            </div>
        </div>
    );
};

export default memo(ServicesSettings);
